import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';

let isInitialized = false;

/**
 * Initialize Amplitude analytics.
 * Should be called once when the app loads.
 */
export function initAnalytics(): void {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;

  // Only initialize in production or if API key is provided
  if (!apiKey) {
    console.log('[Analytics] No Amplitude API key found, analytics disabled');
    return;
  }

  if (isInitialized) {
    return;
  }

  amplitude.init(apiKey, undefined, {
    defaultTracking: {
      sessions: true,
      pageViews: false, // We'll track page views manually with custom event names
      formInteractions: false,
      fileDownloads: false,
    },
  });

  isInitialized = true;
  console.log('[Analytics] Amplitude initialized');
}

/**
 * Track an event with Amplitude.
 * This is a no-op if Amplitude is not initialized.
 */
export function track(eventName: string, eventProperties?: Record<string, unknown>): void {
  if (!isInitialized) {
    // Log in dev mode for debugging
    if (import.meta.env.DEV) {
      console.log('[Analytics] Would track:', eventName, eventProperties ?? '');
    }
    return;
  }

  const merged: Record<string, unknown> = { ...(eventProperties ?? {}) };
  if (typeof window !== 'undefined' && typeof window.location?.pathname === 'string') {
    if (typeof merged.path !== 'string') {
      merged.path = window.location.pathname;
    }
    if (typeof merged.seo_category !== 'string') {
      const path = window.location.pathname;
      if (path.startsWith('/compare')) merged.seo_category = 'compare';
      else if (path.startsWith('/parenting-plan')) merged.seo_category = 'parenting-plan';
      else if (path.startsWith('/child-support-calculator')) merged.seo_category = 'child-support';
      else if (path.startsWith('/learn/')) merged.seo_category = 'learn';
      else if (path === '/privacy-policy' || path === '/terms-of-service') {
        merged.seo_category = 'legal';
      } else if (
        path === '/custody-schedule-app' ||
        path === '/visitation-tracker' ||
        path === '/coparent-money' ||
        path === '/court-ready-records'
      ) {
        merged.seo_category = 'seo-landing';
      } else {
        merged.seo_category = 'core';
      }
    }
  }
  amplitude.track(eventName, merged);
}

/**
 * User properties to set in Amplitude.
 */
export type UserProperties = {
  id: number;
  created_at?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  date_of_birth?: string | null;
  subscribed?: boolean;
};

/**
 * Identify a user and set their properties in Amplitude.
 * Call this when a user signs in or when user data is available.
 */
export function identifyUser(user: UserProperties): void {
  if (!isInitialized) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Would identify user:', user);
    }
    return;
  }

  // Set the user ID - Amplitude requires at least 5 characters
  // Prefix with "user_" to ensure minimum length
  const userIdString = `user_${user.id}`;
  console.log('[Analytics] identifyUser:', userIdString);
  amplitude.setUserId(userIdString);

  // Build the Identify object with user properties
  const identify = new Identify();

  if (user.created_at) {
    identify.set('created_at', user.created_at);
  }
  if (user.first_name) {
    identify.set('first_name', user.first_name);
  }
  if (user.last_name) {
    identify.set('last_name', user.last_name);
  }
  if (user.email) {
    identify.set('email', user.email);
  }
  if (user.phone_number) {
    identify.set('phone_number', user.phone_number);
  }
  if (user.date_of_birth) {
    identify.set('date_of_birth', user.date_of_birth);
  }
  if (typeof user.subscribed === 'boolean') {
    identify.set('subscribed', user.subscribed);
  }

  // Send the identify call
  amplitude.identify(identify);
}

/**
 * Set user properties for analytics.
 * These properties will be attached to all future events from this user.
 * @deprecated Use identifyUser instead for setting user properties along with user ID.
 */
export function setUserProperties(
  userProperties: Record<string, unknown>
): void {
  if (!isInitialized) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Would set user properties:', userProperties);
    }
    return;
  }

  const identify = new Identify();
  Object.entries(userProperties).forEach(([key, value]) => {
    // Amplitude's set method accepts string, number, boolean, or string[]
    if (value !== null && value !== undefined) {
      identify.set(key, value as string | number | boolean | string[]);
    }
  });
  amplitude.identify(identify);
}

/**
 * Clear user identity (call on sign out).
 */
export function resetUser(): void {
  if (!isInitialized) {
    return;
  }

  amplitude.reset();
}
